<template>
  <section class="auth-form">
    <div class="auth-form__invitation" v-if="!error.type">
      <AuthenticationBackground
        :companyLogo="accountLogo"
        :companyName="invitationCompanyName"
        :isBusiness="true"
      />
      <div class="auth-form__wrapper auth-form__wrapper-business">
        <div v-if="isLoading" class="auth-form__spinner-wrapper">
          <ActionLoader />
        </div>
        <div v-else class="auth-form__wrapper--centered auth-form__form">
          <div class="auth-form__selector" v-if="!userInvitation">
            <div class="auth-form__selector-wrapper">
              <button
                class="btn"
                :class="
                  currentForm === 'register' ? 'btn--primary' : 'btn--secondary'
                "
                @click="currentForm = 'register'"
              >
                Sign up
              </button>
              <button
                class="btn"
                :class="
                  currentForm === 'login' ? 'btn--primary' : 'btn--secondary'
                "
                @click="currentForm = 'login'"
              >
                Sign in
              </button>
            </div>
          </div>
          <transition-group name="fade" tag="div" class="auth-form__forms">
            <BusinessLoginForm
              v-if="currentForm === 'login'"
              :isLoading="isLoadingCaptcha"
              :authProviders="authProviders"
              :invitation="invitationInfo"
              @submit-company="loginWithFederated"
              @submit-form="fetchLoginRecaptcha"
              @login-through-provider="loginThroughProvider"
              key="login"
              class="auth-form__form"
            />
            <BusinessRegistrationForm
              v-if="currentForm === 'register'"
              :isLoading="isLoadingCaptcha"
              :interests="allowedInterests"
              :isAuthenticated="isAuthenticated"
              :profile="profile || {}"
              :invitation="invitationInfo"
              @submit-form="fetchRegisterRecaptcha"
              key="register"
              class="auth-form__form"
            />
          </transition-group>
          <div class="auth-form__captcha-wrapper">
            <vue-recaptcha
              ref="invisibleRecaptcha"
              :sitekey="recaptchaSitekey"
              recaptchaHost="www.recaptcha.net"
              @verify="onCaptchaVerify"
              @expired="onCaptchaExpire"
              size="invisible"
              :loadRecaptchaScript="true"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="auth-form__error" v-else>
      <ErrorInvites v-if="error.type === 'error'" />
      <InfoInvites v-if="error.type === 'info'" />
      <h3 class="auth-form__error__title">Something went wrong!</h3>
      <div class="auth-form__error__message">{{ error.message }}</div>
    </div>
  </section>
</template>

<script>
import { mapState } from 'vuex'
import VueRecaptcha from 'vue-recaptcha'
import AuthenticationBackground from '@/components/forms/elements/AuthenticationBackground.vue'
import BusinessLoginForm from '@/components/forms/business/BusinessLoginForm.vue'
import BusinessRegistrationForm from '@/components/forms/business/BusinessRegistrationForm.vue'
import ActionLoader from '@/components/shared/ActionLoader.vue'
import CONSTANTS from '@/constants'
import ErrorInvites from '@/assets/icons/business/error_icon_invites.svg'
import InfoInvites from '@/assets/icons/business/info_icon_invites.svg'

const createDOMPurify = require('dompurify')

const DOMPurify = createDOMPurify(window)

export default {
  name: 'Login',
  metaInfo() {
    return {
      title: `INE - Login`,
      meta: [
        {
          vmid: 'robots',
          name: 'robots',
          content: 'noindex',
        },
      ],
    }
  },
  components: {
    AuthenticationBackground,
    BusinessLoginForm,
    VueRecaptcha,
    BusinessRegistrationForm,
    ActionLoader,
    ErrorInvites: { ...ErrorInvites, name: 'ErrorInvites' },
    InfoInvites: { ...InfoInvites, name: 'InfoInvites' },
  },
  data() {
    return {
      isLoading: true,
      isLoadingCaptcha: false,
      from: null,
      recaptcha: '',
      email: '',
      password: '',
      currentForm: 'login',
      registrationFields: null,
      invitationId: null,
      error: {
        type: null,
        message: null,
      },
    }
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.from = from
    })
  },
  computed: {
    ...mapState({
      isAuthenticated: state => state.auth.isAuthenticated,
      isTwoFactorRequired: state => state.auth.isTwoFactorRequired,
      isPageLoading: state => state.app.isPageLoading,
      authProviders: state => state.auth.authProviders,
      redirectUrl: state => state.auth.redirectUrl,
      profile: state => state.auth.userProfile,
      interests: state => state.subscriptions.passTypes,
      invitationInfo: state => state.business.invitationInfo,
    }),
    recaptchaSitekey() {
      return process.env.VUE_APP_RECAPTCHA_SITEKEY
    },
    allowedInterests() {
      return this.interests.filter(i => CONSTANTS.INTERESTS.includes(i.id))
    },
    userInvitation() {
      return this.invitationInfo?.user_invite
    },
    accountLogo() {
      return this.invitationInfo?.account?.logo_url || ''
    },
    invitationCompanyName() {
      return this.invitationInfo?.account.name
    },
  },
  methods: {
    onCaptchaVerify(token) {
      this.recaptcha = token
      if (this.currentForm === 'login') this.submitLoginForm()
      if (this.currentForm === 'register') this.submitRegisterForm()
      this.isLoadingCaptcha = false
    },
    onCaptchaExpire() {
      this.recaptcha = ''
      this.$refs.invisibleRecaptcha.reset()
    },
    fetchLoginRecaptcha({ email, password }) {
      this.isLoadingCaptcha = true
      this.email = email
      this.password = password
      this.$refs.invisibleRecaptcha.execute()
    },
    fetchRegisterRecaptcha(registrationFields) {
      this.isLoadingCaptcha = true
      this.registrationFields = registrationFields
      this.$refs.invisibleRecaptcha.execute()
    },
    async submitLoginForm() {
      const { email, password, recaptcha } = this
      try {
        this.isLoading = true
        const payload = { email, password, recaptcha }
        await this.$store.dispatch('auth/login', payload)

        const { isTwoFactorRequired, isAuthenticated } = this
        if (isTwoFactorRequired) {
          this.$store.dispatch(
            'business/setBusinessInvitationId',
            this.invitationId
          )
          this.$router.push({ name: 'two-factor' })
          return
        }

        if (isAuthenticated) {
          this.sendMixpanelEvent(this.eventsConstants.ACCOUNT_USER_LOGIN, {})
          this.$router.push({
            name: 'confirm-invite',
            params: { id: this.invitationId },
          })
        }
      } catch (error) {
        this.onCaptchaExpire()
      }
      this.isLoading = false
    },
    async loginWithFederated(company) {
      try {
        this.isLoading = true
        const response = await this.$store.dispatch(
          'auth/getFederatedUrl',
          company
        )
        if (response.login_url) {
          if (this.invitationId)
            localStorage.setItem('business-invitation-id', this.invitationId)
          window.location.assign(response.login_url)
        }
      } catch (error) {
        this.isLoading = false
      }
    },
    async submitRegisterForm() {
      try {
        this.isLoading = true
        const { email, first_name, last_name } = this.registrationFields
        const { recaptcha } = this
        const payload = {
          id: this.invitationId,
          email,
          first_name,
          last_name,
          recaptcha,
        }
        if (!this.isAuthenticated) {
          payload.password = this.registrationFields.password
        }
        await this.$store.dispatch('business/signUpFromInvitation', payload)
        await this.$store.dispatch('auth/login', payload)
        this.$router.push({
          name: 'confirm-invite',
          params: { id: this.invitationId },
        })
      } catch (error) {
        this.onCaptchaExpire()
      } finally {
        this.isLoading = false
      }
    },
    loginThroughProvider(provider) {
      this.isLoading = true
      const { url } = provider
      if (this.invitationId)
        localStorage.setItem('business-invitation-id', this.invitationId)
      this.$store.dispatch('auth/saveRedirectUrl')
      window.open(url, '_parent')
    },
    checkForParams(params) {
      if (params.has('error')) {
        this.$store.dispatch(
          'app/setError',
          DOMPurify.sanitize(params.get('error'), {
            ALLOWED_TAGS: [],
          })
        )
      }
      if (params.has('redirect_url')) {
        const redirectUrl = this.buildRedirectUrl(params)
        this.$store.dispatch('auth/setRedirectUrl', redirectUrl)
      }
    },
    buildRedirectUrl(params) {
      params.delete('error')
      let buildString = params.get('redirect_url')
      params.delete('redirect_url')
      const entries = [...params.entries()]
      if (entries && entries.length) {
        const allParams = entries.reduce((acc, [key, value]) => {
          if (Array.isArray(value)) {
            acc.push(`${key}=${value.join()}`)
          } else {
            acc.push(`${key}=${value}`)
          }
          return acc
        }, [])
        buildString += `?${allParams.join('&')}`
      }
      return buildString
    },
  },
  async created() {
    localStorage.removeItem('business-invitation-id')
    this.invitationId = this.$route.params?.id
    if (!this.invitationId) {
      return this.$router.push({ name: 'home' })
    }
    this.isLoading = true
    try {
      await this.$store.dispatch('business/getInvitation', {
        id: this.invitationId,
      })
      this.isLoading = false
      if (this.invitationInfo.is_active) {
        if (
          this.invitationInfo.user_invite &&
          !this.invitationInfo.user_invite.is_member
        ) {
          this.currentForm = 'register'
        }
        if (this.isAuthenticated) {
          return this.$router.push({
            name: 'confirm-invite',
            params: { id: this.invitationId },
          })
        }
      } else {
        return this.$router.push({ name: 'invite-expired' })
      }
    } catch (error) {
      this.error.type = 'error'
      this.error.message = 'This invite is no longer valid.'
      this.isLoading = false
    }
    if (this.isTwoFactorRequired) {
      this.$store.dispatch(
        'business/setBusinessInvitationId',
        this.invitationId
      )
      this.$router.push({ name: 'two-factor' })
    }
  },
}
</script>
<style lang="scss" scoped></style>
